import React, { useState, useEffect } from 'react';
import pushNotifications from '../Utils/pushNotifications';
import { showToast } from '../Utils/toast';
import { Bell, BellOff } from 'lucide-react';

const NotificationSettings = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkNotificationStatus();
  }, []);

  const checkNotificationStatus = async () => {
    const supported = pushNotifications.isSupported();
    setIsSupported(supported);

    if (supported) {
      const perm = pushNotifications.getPermissionStatus();
      setPermission(perm);

      const subscribed = await pushNotifications.isSubscribed();
      setIsSubscribed(subscribed);
    }
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    try {
      await pushNotifications.subscribe();
      setIsSubscribed(true);
      setPermission('granted');
      showToast.success('Notifications enabled!');
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      if (error.message.includes('permission')) {
        showToast.error('Please allow notifications in your browser settings');
      } else {
        showToast.error('Failed to enable notifications');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setLoading(true);
    try {
      await pushNotifications.unsubscribe();
      setIsSubscribed(false);
      showToast.success('Notifications disabled');
    } catch (error) {
      console.error('Failed to disable notifications:', error);
      showToast.error('Failed to disable notifications');
    } finally {
      setLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="notification-settings p-4 bg-gray-800 rounded-lg">
        <p className="text-gray-400 text-sm">
          Push notifications are not supported in this browser
        </p>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="notification-settings p-4 bg-gray-800 rounded-lg">
        <p className="text-red-400 text-sm">
          Notifications are blocked. Please enable them in your browser settings.
        </p>
      </div>
    );
  }

  return (
    <div className="notification-settings p-4 bg-gray-800 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <Bell className="w-5 h-5 text-green-400" />
          ) : (
            <BellOff className="w-5 h-5 text-gray-400" />
          )}
          <div>
            <h3 className="font-medium text-white">Browser Notifications</h3>
            <p className="text-sm text-gray-400 mt-1">
              {isSubscribed
                ? 'You will receive notifications for new songs and updates'
                : 'Enable to get notified about new songs from people you follow'
              }
            </p>
          </div>
        </div>

        <button
          onClick={isSubscribed ? handleDisableNotifications : handleEnableNotifications}
          disabled={loading}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            isSubscribed
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : 'bg-green-500 hover:bg-green-600 text-white'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {loading ? 'Loading...' : isSubscribed ? 'Disable' : 'Enable'}
        </button>
      </div>
    </div>
  );
};

export default NotificationSettings;
